import { Button, useAlert } from '@ads/components-react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as z from 'zod';

import { PlanEditProvider } from '@/features/individual-payment/context/PlanEditContext';
import { usePlanEditor } from '@/features/individual-payment/hooks/usePlanEditor';
import type { PlanFormValues } from '@/features/individual-payment/hooks/usePlanFormSchema';
import { usePlanFormSchema } from '@/features/individual-payment/hooks/usePlanFormSchema';
import { useSavePlan } from '@/features/individual-payment/hooks/useSavePlan';
import type { PaymentHistoryMode } from '@/features/individual-payment/types/paymentHistory';
import { PlanAgreementDocsTab } from '@/features/individual-payment/ui/organisms/PlanAgreementDocsTab';
import { PlanDetailsTab } from '@/features/individual-payment/ui/organisms/PlanDetailsTab';
import { PlanHistoryDrawer } from '@/features/individual-payment/ui/organisms/PlanHistoryDrawer';
import { PlanValuesTab } from '@/features/individual-payment/ui/organisms/PlanValuesTab';
import { UpdateParcelsConfirmModal } from '@/features/individual-payment/ui/organisms/UpdateParcelsConfirmModal';
import type { PlanEditTab } from '@/features/individual-payment/ui/templates/PlanEditTemplate';
import {
  PLAN_EDIT_TABS,
  PlanEditTemplate,
} from '@/features/individual-payment/ui/templates/PlanEditTemplate';
import { buildPlanSavePayload } from '@/features/individual-payment/utils/buildPlanSavePayload';
import { EMPTY_VALUE } from '@/features/individual-payment/utils/formatCurrency';

/**
 * A aba ativa vive na search param, não em estado local: assim sobrevive a
 * recarga e ao botão voltar, é linkável, e apontar "o erro está na aba Valores"
 * é só navegar.
 */
const searchSchema = z.object({
  tab: z.enum(['detalhes', 'valores', 'documentos']).default('detalhes'),
});

/**
 * Valida o `tab` vindo da URL sem confiar no schema da rota.
 *
 * O valor cru é `unknown` de propósito: se o `validateSearch` não tiver sido
 * aplicado, `search.tab` pode chegar `undefined` ou como qualquer string.
 */
function isPlanEditTab(value: unknown): value is PlanEditTab {
  return typeof value === 'string' && (PLAN_EDIT_TABS as readonly string[]).includes(value);
}

export const Route = createFileRoute('/_authenticated/individual-payment/$financialPersonId/')({
  validateSearch: searchSchema,
  component: IndividualPaymentPlanEditPage,
});

/**
 * Página de edição do plano financeiro de uma pessoa.
 *
 * Um único formulário atende as três abas, porque o salvamento é um `PUT` só. O
 * corpo desse `PUT` parte da resposta do editor — não do formulário — para que
 * os campos que a tela não expõe voltem intactos.
 */
export function IndividualPaymentPlanEditPage() {
  const { t } = useTranslation('individualPayment');
  const { alert } = useAlert();
  const navigate = useNavigate();
  const { financialPersonId: rawId } = Route.useParams();
  const search = Route.useSearch();

  // A aba que a tela renderiza é estado local, com a URL como espelho.
  //
  // Depender só do search param deixava a tela travada na primeira aba: se o
  // param não voltar validado (bundle antigo sem `validateSearch`, param
  // removido por outra camada), `search.tab` vem `undefined` — nenhuma aba fica
  // ativa e nenhum conteúdo renderiza, mesmo com a URL correta.
  //
  // Assim o clique responde na hora e a URL continua servindo deep link e
  // back/forward, sem que o render dependa do round-trip do router.
  const urlTab = isPlanEditTab(search?.tab) ? search.tab : 'detalhes';
  const [tab, setTab] = useState<PlanEditTab>(urlTab);

  // Sincroniza quando a URL muda por fora do clique: deep link, voltar, avançar.
  useEffect(() => {
    setTab(urlTab);
  }, [urlTab]);

  // O parâmetro de rota é string. Converter na fronteira evita propagar `NaN`
  // para a URL das requisições.
  const financialPersonId = useMemo(() => {
    const parsed = Number(rawId);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [rawId]);

  const { editor, isLoading, isError, refetch } = usePlanEditor(financialPersonId);
  const { schema, defaultValues } = usePlanFormSchema(editor);
  const { save, isPending, isForbidden } = useSavePlan(financialPersonId);

  const form = useForm<PlanFormValues>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  // O editor resolve depois do primeiro render, então os defaults chegam atrasados.
  useEffect(() => {
    if (editor) form.reset(defaultValues);
  }, [editor, defaultValues, form]);

  const [isConfirmOpen, setConfirmOpen] = useState(false);

  // Painel de histórico do plano. Vive na página, não no template: é uma camada
  // sobre toda a tela, e só um dos dois históricos fica aberto por vez — o modo
  // aberto é o que decide qual query é habilitada.
  const [history, setHistory] = useState<PaymentHistoryMode | null>(null);

  const openParcelsCount = Math.max((editor?.numberParcels ?? 0) - (editor?.closedParcels ?? 0), 0);

  const submit = (values: PlanFormValues) => {
    save(buildPlanSavePayload(editor, values), {
      onSuccess: () => {
        setConfirmOpen(false);
        alert({ alertType: 'success', title: t('planEdit.saveSuccess') });
      },
      onError: error => {
        // `5xx` não tem tratamento global — o interceptor só repassa o erro. Sem
        // este alerta, o Salvar falhava calado: o operador via o botão voltar ao
        // normal e supunha que tinha gravado.
        if (error.kind === 'server') {
          alert({ alertType: 'danger', title: t('planEdit.serverError') });
        }
      },
    });
  };

  const handleSave = () => {
    void form.handleSubmit(values => {
      // Recriar parcelas é irreversível: quando o operador pediu isso, a
      // confirmação vem antes da request, nunca depois.
      if (values.updatePlanParcels) {
        setConfirmOpen(true);
        return;
      }
      submit(values);
    })();
  };

  const goToTab = (next: PlanEditTab) => {
    // Estado primeiro: a aba troca sem esperar o router.
    setTab(next);
    void navigate({
      to: '/individual-payment/$financialPersonId',
      params: { financialPersonId: rawId },
      search: { tab: next },
    });
  };

  if (financialPersonId === null) {
    return (
      <div className="flex flex-col items-center gap-micro rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xl">
        <span className="flex items-center justify-center rounded-pill bg-base-danger-100 p-micro">
          <ExclamationTriangleIcon className="h-xxs w-xxs text-base-danger-700" />
        </span>
        <p className="ts-heading-xs text-ctx-content-title">{t('planEdit.invalidId')}</p>
        <p className="ts-paragraph-xs text-ctx-content-placeholder">
          {t('planEdit.invalidIdHint')}
        </p>
        <Button
          hierarchy="secondary"
          size="md"
          onClick={() => void navigate({ to: '/individual-payment' })}
        >
          {t('planEdit.backToList')}
        </Button>
      </div>
    );
  }

  // Falha na carga não monta o formulário: um form vazio salvaria valores errados.
  if (isError) {
    return (
      <div className="flex flex-col items-center gap-micro rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xl">
        <span className="flex items-center justify-center rounded-pill bg-base-danger-100 p-micro">
          <ExclamationTriangleIcon className="h-xxs w-xxs text-base-danger-700" />
        </span>
        <p className="ts-heading-xs text-ctx-content-title">{t('planEdit.error.title')}</p>
        <p className="ts-paragraph-xs text-ctx-content-placeholder">{t('planEdit.error.hint')}</p>
        <Button hierarchy="secondary" size="md" onClick={() => void refetch()}>
          {t('planEdit.error.retry')}
        </Button>
      </div>
    );
  }

  return (
    <PlanEditProvider
      mode="edit"
      form={form}
      editor={editor}
      financialPersonId={financialPersonId}
      isSaving={isPending}
      isReadOnly={isForbidden}
    >
      <PlanEditTemplate
        studentName={editor?.student ?? EMPTY_VALUE}
        courseName={editor?.courseName}
        activeTab={tab}
        isLoading={isLoading}
        onTabChange={goToTab}
        onOrderTracking={() => setHistory('orderTracking')}
        onOperationsTracking={() => setHistory('traceability')}
        onViewParcels={() =>
          void navigate({
            to: '/individual-payment/$financialPersonId/parcels',
            params: { financialPersonId: rawId },
          })
        }
        onSave={handleSave}
        onCancel={() => void navigate({ to: '/individual-payment' })}
      >
        {isForbidden ? (
          <div className="flex flex-col gap-quark rounded-sm border-hairline border-base-gray-300 bg-base-gray-100 px-xxs py-micro">
            <span className="ts-paragraph-xs !font-semibold text-ctx-content-title">
              {t('planEdit.forbidden.title')}
            </span>
            <span className="ts-paragraph-xxs text-base-gray-700">
              {t('planEdit.forbidden.hint')}
            </span>
          </div>
        ) : null}

        {tab === 'detalhes' ? <PlanDetailsTab /> : null}
        {tab === 'valores' ? <PlanValuesTab /> : null}
        {tab === 'documentos' ? <PlanAgreementDocsTab /> : null}
      </PlanEditTemplate>

      <PlanHistoryDrawer
        financialPersonId={financialPersonId}
        mode={history}
        studentName={editor?.student}
        onClose={() => setHistory(null)}
      />

      <UpdateParcelsConfirmModal
        open={isConfirmOpen}
        openParcelsCount={openParcelsCount}
        isPending={isPending}
        onConfirm={() => submit(form.getValues())}
        onClose={() => setConfirmOpen(false)}
      />
    </PlanEditProvider>
  );
}
